import { stringifyError } from './error';

// An attached wallet declares parentWalletId: it belongs to another wallet, one level only (a parent
// is never attached itself, and group wallets are neither parents nor children). The first kind is
// the Arc yield wallet: a DeFi wallet tracking rewards that accrue on Arc in a wallet custodied at
// BitGo. The parent is that BitGo Connection wallet; a backend job books the earnings as
// transactions on the parent, so the attached wallet never has transactions of its own.

export const CONNECTION_WALLET_TYPE = 20;
export const DEFI_WALLET_TYPE = 22;
export const GROUP_WALLET_TYPE = 999;

// Arc is Circle's EVM L1. The "yield" vault moniker declares the protocol, like "traffic" does for
// Canton, so an Arc yield wallet has no contract address of its own.
export const ARC_NETWORK_ID = 'arc';
export const YIELD_VAULT_MONIKER = 'yield';
export const YIELD_PROTOCOL = 'Yield';

export type AccrualCadence = 'hourly' | 'daily';

type ParentCandidate = {
  id?: string | null;
  name?: string | null;
  type?: number | null;
  deleted?: boolean | null;
  disabled?: boolean | null;
  parentWalletId?: string | null;
  networkId?: string | null;
  connectionId?: string | null;
  exchangeConnectionId?: string | null;
  address?: string | null;
  addresses?: Array<string | null> | null;
  metadata?: object | null;
};

type ConnectionProvider = { id: string; provider?: string | null };

export type ParentWalletOption = { id: string; name: string; caption: string; address: string };

type Nestable = { id?: string | null; parentWalletId?: string | null };

export type ArcYieldWalletData = {
  parentWalletId?: string;
  walletAddress?: string;
  accrualCadence?: AccrualCadence;
  syncStartDateSEC?: number;
};

const EVM_ADDRESS = /^0x[0-9a-fA-F]{40}$/;
const DATE_INPUT_VALUE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isEvmAddress(value?: string | null): boolean {
  return EVM_ADDRESS.test((value ?? '').trim());
}

// A date input's value (YYYY-MM-DD) as unix seconds at that day's UTC midnight.
export function utcMidnightSEC(date?: string | null): number | undefined {
  const match = DATE_INPUT_VALUE.exec((date ?? '').trim());
  if (!match) return undefined;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) / 1000;
}

// The parent's own EVM address, if it has one, to prefill the attached wallet's. A Connection
// wallet has no address of its own, but a BitGo wallet carries its on-chain address in
// metadata.address (bitgo-sync-svc).
export function parentWalletAddress(wallet: ParentCandidate): string {
  const metadataAddress = (wallet.metadata as Record<string, unknown> | null | undefined)?.address;
  const candidates = [
    wallet.address,
    ...(wallet.addresses ?? []),
    typeof metadataAddress === 'string' ? metadataAddress : null,
  ];
  return (candidates.find((address) => isEvmAddress(address)) ?? '').trim();
}

// The wallets an attached wallet can belong to: not deleted or disabled, not a group, not a DeFi
// wallet and not attached itself. BitGo Connection wallets come first, then other Connection
// wallets, then the rest, each by name. A Connection wallet's provider is its connection's.
export function parentWalletOptions(
  wallets: ParentCandidate[],
  connections: ConnectionProvider[]
): ParentWalletOption[] {
  const providers = new Map(connections.map((connection) => [connection.id, connection.provider ?? '']));
  const options = wallets
    .filter(
      (wallet) =>
        !!wallet.id &&
        !wallet.deleted &&
        !wallet.disabled &&
        wallet.type !== GROUP_WALLET_TYPE &&
        wallet.type !== DEFI_WALLET_TYPE &&
        !wallet.parentWalletId
    )
    .map((wallet) => {
      const isConnection = wallet.type === CONNECTION_WALLET_TYPE;
      const provider = isConnection
        ? providers.get(wallet.exchangeConnectionId ?? '') || providers.get(wallet.connectionId ?? '') || ''
        : '';
      const isBitGo = provider.toLowerCase() === 'bitgo';
      const address = parentWalletAddress(wallet);
      const connectionLabel = isBitGo ? 'BitGo' : provider;
      const caption = isConnection
        ? connectionLabel
          ? `${connectionLabel} connection`
          : 'Connection'
        : address || wallet.networkId || '';
      return {
        tier: isBitGo ? 0 : isConnection ? 1 : 2,
        option: { id: wallet.id as string, name: wallet.name ?? '', caption, address },
      };
    });

  return options
    .sort((a, b) => a.tier - b.tier || a.option.name.localeCompare(b.option.name))
    .map(({ option }) => option);
}

function isNestedUnder(wallet: Nestable, parent?: Nestable): boolean {
  return !!parent && parent !== wallet && !parent.parentWalletId;
}

// Ids of the attached wallets that sit under their parent: the parent is in the list and is not
// attached itself. Orphans, whose parent is not in the list, are not nested.
export function nestedAttachedWalletIds(wallets: Nestable[]): Set<string> {
  const byId = new Map(wallets.map((wallet) => [wallet.id, wallet]));
  const ids = new Set<string>();
  for (const wallet of wallets) {
    if (wallet.id && wallet.parentWalletId && isNestedUnder(wallet, byId.get(wallet.parentWalletId))) {
      ids.add(wallet.id);
    }
  }
  return ids;
}

// Moves each nested attached wallet right after its parent, attached siblings in their existing
// order. Every other wallet, orphans included, keeps its order, so a list without attached wallets
// comes back unchanged.
export function nestAttachedWallets<T extends Nestable>(wallets: T[]): T[] {
  const nestedIds = nestedAttachedWalletIds(wallets);
  if (nestedIds.size === 0) return wallets;

  const children = new Map<string, T[]>();
  for (const wallet of wallets) {
    if (wallet.id && nestedIds.has(wallet.id)) {
      const parentId = wallet.parentWalletId as string;
      children.set(parentId, [...(children.get(parentId) ?? []), wallet]);
    }
  }

  const ordered: T[] = [];
  for (const wallet of wallets) {
    if (wallet.id && nestedIds.has(wallet.id)) continue;
    ordered.push(wallet);
    const attached = wallet.id ? children.get(wallet.id) : undefined;
    if (attached) {
      ordered.push(...attached);
      children.delete(wallet.id as string);
    }
  }
  return ordered;
}

export function isArcYieldWalletDataValid(data?: ArcYieldWalletData): boolean {
  return (
    !!data?.parentWalletId &&
    isEvmAddress(data.walletAddress) &&
    (data.accrualCadence === 'hourly' || data.accrualCadence === 'daily')
  );
}

// createWallet's defi input (and flags) for an Arc yield wallet. The book-from date is sent only
// when one is picked; without it the server books from creation. subsidiaryId, when not sent, is
// inherited from the parent server-side.
export function arcYieldWalletFields(data: ArcYieldWalletData) {
  return {
    defi: {
      networkId: ARC_NETWORK_ID,
      walletAddress: (data.walletAddress ?? '').trim(),
      vaultAddress: YIELD_VAULT_MONIKER,
      protocol: YIELD_PROTOCOL,
      parentWalletId: data.parentWalletId,
      accrualCadence: data.accrualCadence,
      isSyncEnabled: true,
    },
    ...(data.syncStartDateSEC ? { flags: { syncStartDateSEC: data.syncStartDateSEC } } : {}),
  };
}

// Why createWallet failed, e.g. the server refusing the parent or an address already attached
// under another parent, without apollo's "GraphQL error: " prefix.
export function createWalletErrorMessage(error: unknown): string {
  return stringifyError(error, { hideErrorName: true }).replace(/GraphQL error: /g, '');
}
